import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      const user = await register(userData);
      toast.success(`Welcome, ${user.first_name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>CREATE ACCOUNT</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; Register
          </div>
        </div>
      </div>

      <section className="content-section">
        <div className="container-narrow" style={{maxWidth: '500px'}}>
          <p className="text-center text-gray-600 mb-8">
            Join Archon Insurance to manage your policies and file claims online.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">First Name</label>
                <Input name="first_name" placeholder="John" value={formData.first_name} onChange={handleChange} required className="form-input" data-testid="register-first-name" />
              </div>
              <div>
                <label className="form-label">Last Name</label>
                <Input name="last_name" placeholder="Doe" value={formData.last_name} onChange={handleChange} required className="form-input" data-testid="register-last-name" />
              </div>
            </div>
            <div>
              <label className="form-label">Email Address</label>
              <Input name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required className="form-input" data-testid="register-email" />
            </div>
            <div>
              <label className="form-label">Phone (Optional)</label>
              <Input name="phone" type="tel" placeholder="(617) 555-0123" value={formData.phone} onChange={handleChange} className="form-input" data-testid="register-phone" />
            </div>
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Input name="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" value={formData.password} onChange={handleChange} required className="form-input pr-10" data-testid="register-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="form-label">Confirm Password</label>
              <Input name="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} required className="form-input" data-testid="register-confirm-password" />
            </div>
            <button type="submit" className="btn-navy w-full flex items-center justify-center" disabled={loading} data-testid="register-submit-btn">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</> : 'Create Account'}
            </button>
          </form>

          <hr className="content-divider" />

          <p className="text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1a365d] font-semibold" data-testid="login-link">Sign in →</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RegisterPage;
